import { motion } from 'motion/react';
import { CheckCircle } from 'lucide-react';

interface SuccessAnimationProps {
  message?: string;
  onComplete?: () => void;
}

export function SuccessAnimation({ message = 'Success!', onComplete }: SuccessAnimationProps) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ 
        duration: 0.5,
        type: "spring",
        stiffness: 200,
        damping: 15
      }}
      onAnimationComplete={() => {
        if (onComplete) {
          setTimeout(onComplete, 1000);
        }
      }}
      className="flex flex-col items-center justify-center gap-4 py-8"
    >
      {/* Checkmark Icon */}
      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 0.2, duration: 0.5, type: "spring" }}
      >
        <CheckCircle className="w-20 h-20 text-green-500" strokeWidth={2} />
      </motion.div>

      {/* Success Message */}
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        className="text-2xl font-bold text-slate-900"
      >
        {message}
      </motion.h2>

      {/* Confetti Effect */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            initial={{ 
              x: '50%', 
              y: '50%',
              opacity: 1,
              scale: 0
            }}
            animate={{ 
              x: `${50 + (Math.random() - 0.5) * 100}%`,
              y: `${50 + (Math.random() - 0.5) * 100}%`,
              opacity: 0,
              scale: 1,
              rotate: Math.random() * 360
            }}
            transition={{ 
              delay: i * 0.02,
              duration: 0.8,
              ease: "easeOut"
            }}
            className="absolute w-2 h-2 rounded-full"
            style={{
              backgroundColor: ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'][i % 5],
            }}
          />
        ))}
      </div>
    </motion.div>
  );
}
